import {
  Button,
  Grid,
  Stack,
  TextField,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Alert,
  AlertTitle,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  OrganizationState,
  useOrganizationState,
} from 'features/organizations/state/useOrganizationState';
import { RecurlyCardElement } from 'features/payments/components/RecurlyBillingInformation/RecurlyCardElement';
import { Controller, useForm } from 'react-hook-form';
import { useRecurly } from '@recurly/react-recurly';
import { getCreditCardValidationErrors } from 'features/payments/components/RecurlyBillingInformation/utils';
import { useOrganizationUpgrade } from 'features/organizations/hooks/useOrganizationUpgrade';
import { useState } from 'react';
import * as Sentry from '@sentry/capacitor';
import { useOrganizationDetails } from 'features/organizations/hooks/useOrganizationDetails';
import { InlineStatus } from 'components/InlineStatus';
import { useSavedPaymentMethod } from 'hooks/useSavedPaymentMethod';
import { RecurlySavedPayment } from 'features/payments/components/RecurlySavedPayment';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import {
  isRecurlyError,
  parseRecurlyErrorToTransKey,
} from 'features/payments/payments.utils';
import { getOrgCommonSignupFormsValidationSchema } from 'features/organizations/organizations.utils';
import { yupResolver } from '@hookform/resolvers/yup';
import { useAuthState } from 'state/useAuthState';
import { useHistory, useParams } from 'react-router-dom';
import { logUnexpectedErrorAndNotifyUserWithTryAgain } from 'shared/handleUnexpectedError';
import { EmailStatus } from 'shared/getEmailValidationSchema';
import { OrganizationUpgradeSummary } from '../OrganizationUpgradeSummary';

interface OrganizationUpgradeBillingData {
  postalCode: string;
}

type OrganizationUpgradeBillingInfoFormProps = {
  onSubmit: (values: Partial<OrganizationState>) => void;
};

export const OrganizationUpgradeBillingInfoForm = (
  props: OrganizationUpgradeBillingInfoFormProps,
): JSX.Element => {
  const { onSubmit } = props;
  const { t } = useTranslation();
  const recurly = useRecurly();
  const [errors, setErrors] = useState<string[]>([]);
  const { processOrganizationUpgrade } = useOrganizationUpgrade({});
  const orgState = useOrganizationState();
  const { new: useNewPaymentMethod } = useParams<{ new?: string }>();
  const { user } = useAuthState();
  const history = useHistory();

  const { savedPaymentMethod, isLoading: isLoadingPaymentMethod } =
    useSavedPaymentMethod({
      useLoggedInState: true,
      paymentType: 'credit_card',
    });

  const {
    details,
    isLoading: detailsLoading,
    isError: detailsError,
  } = useOrganizationDetails();

  const hasErrors = errors.length > 0;

  const handleToggleUseNewPaymentMethod = (): void => {
    if (useNewPaymentMethod) {
      history.replace('/organization/upgrade/billing');
    } else {
      history.push('/organization/upgrade/billing/new');
    }
    setErrors([]);
  };

  const usingSavedPaymentMethod = savedPaymentMethod && !useNewPaymentMethod;

  const { control, handleSubmit, formState } =
    useForm<OrganizationUpgradeBillingData>({
      resolver: yupResolver(
        getOrgCommonSignupFormsValidationSchema({
          t,
          requireExistingUserToLogin: Boolean(user),
          emailValidationCache: new Map<string, EmailStatus>(),
          fields: usingSavedPaymentMethod ? [] : ['postalCode'], // No need to validate postal code if using saved payment method
        }),
      ),
      defaultValues: {
        postalCode: orgState.postalCode || '',
      },
    });

  const submitToken = async (
    values: OrganizationUpgradeBillingData,
    authorizationToken?: string,
  ): Promise<void> => {
    if (!details) {
      throw new Error('details-not-loaded');
    }

    const { seats } = details;
    const totalRegularSeats = seats.regular.purchased + orgState.regularSeats;
    const totalProSeats = seats.pro.purchased + orgState.proSeats;

    await processOrganizationUpgrade({
      ...(authorizationToken ? { authorizationToken } : {}),
      regularSeats: totalRegularSeats,
      proSeats: totalProSeats,
    });

    onSubmit({
      ...orgState,
      ...values,
    });
  };

  const handleSubmissionError = (error: unknown): void => {
    Sentry.captureException(error);
    if (error && isRecurlyError(error)) {
      const validationErrors = getCreditCardValidationErrors(error, t);
      if (validationErrors.length > 0) {
        setErrors(validationErrors);
        return;
      }
    }
    const key = parseRecurlyErrorToTransKey(error);
    if (key) {
      setErrors([t(key)]);
      return;
    }

    logUnexpectedErrorAndNotifyUserWithTryAgain(t, { error });
  };

  const handleFormSubmit = async (
    values: OrganizationUpgradeBillingData,
  ): Promise<void> => {
    try {
      if (usingSavedPaymentMethod) {
        await submitToken(values);
      } else {
        // Generate new payment token
        const token = await new Promise<string>((resolve, reject) => {
          recurly.token(
            {
              first_name: orgState.firstName,
              last_name: orgState.lastName,
              postal_code: values.postalCode,
            },
            (err, recurlyToken) => {
              if (err) {
                const validationErrors = getCreditCardValidationErrors(err, t);
                setErrors(validationErrors);
                reject(err);
              } else {
                resolve(recurlyToken.id);
              }
            },
          );
        });
        await submitToken(values, token);
      }
    } catch (error) {
      handleSubmissionError(error);
    }
  };

  if (detailsLoading || detailsError || isLoadingPaymentMethod) {
    return <InlineStatus status={detailsError ? 'error' : 'loading'} />;
  }

  return (
    <Stack
      spacing={2}
      sx={{ flex: 1, pt: 1 }}
      component="form"
      noValidate
      onSubmit={handleSubmit(handleFormSubmit)}
    >
      <Stack spacing={2}>
        <Grid>
          <Typography variant="h3">
            <b>{t('organizationPurchase.billingInfo')}</b>
          </Typography>
        </Grid>
        {hasErrors && (
          <Grid>
            <Alert icon={false} severity="error" sx={{ color: 'error.main' }}>
              <AlertTitle>{t('recurlyBillingInformation.error')}</AlertTitle>
              <List dense disablePadding sx={{ listStyleType: 'disc', pl: 3 }}>
                {errors.map((error) => (
                  <ListItem
                    key={error}
                    dense
                    disablePadding
                    sx={{ display: 'list-item' }}
                  >
                    <ListItemText
                      primary={error}
                      sx={{ margin: 0 }}
                      slotProps={{
                        primary: {
                          variant: 'body2',
                          fontWeight: 'bold',
                        },
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </Alert>
          </Grid>
        )}

        {usingSavedPaymentMethod ? (
          <>
            <Grid>
              <RecurlySavedPayment paymentMethod={savedPaymentMethod} />
            </Grid>
            {/* Using structure from exsting RecurlyAccountInformation page */}
            <Grid>
              <Typography variant="h3" sx={{ marginTop: 4 }}>
                {t('organizationPurchase.updatePaymentMethod')}
              </Typography>
            </Grid>

            <Grid>
              <List>
                <ListItem disablePadding>
                  <ListItemButton onClick={handleToggleUseNewPaymentMethod}>
                    <ListItemText
                      primary={t('recurlyAccountInformation.button')}
                    />
                    <ListItemIcon sx={{ minWidth: 0 }}>
                      <ArrowForwardIosIcon color="inherit" />
                    </ListItemIcon>
                  </ListItemButton>
                </ListItem>
              </List>
            </Grid>
          </>
        ) : (
          <>
            <Grid>
              <RecurlyCardElement error={hasErrors} />
            </Grid>
            <Grid size={6}>
              <Controller
                name="postalCode"
                control={control}
                render={({ field }) => (
                  <TextField
                    fullWidth
                    label={t('organizationPurchase.zipcode.label')}
                    required
                    error={Boolean(formState.errors.postalCode)}
                    helperText={formState.errors.postalCode?.message}
                    {...field}
                  />
                )}
              />
            </Grid>
          </>
        )}

        <OrganizationUpgradeSummary
          regularSeats={orgState.regularSeats}
          proSeats={orgState.proSeats}
        />
      </Stack>
      <Stack sx={{ flexGrow: 1, justifyContent: 'flex-end' }}>
        <Grid>
          <Button
            size="large"
            fullWidth
            type="submit"
            disabled={formState.isSubmitting}
          >
            {t('organizationPurchase.purchase')}
          </Button>
        </Grid>
      </Stack>
    </Stack>
  );
};
